import {HttpClient} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {EMPTY, interval, map, startWith, switchMap, takeWhile, tap} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {SnackbarService} from './snackbar.service';

const VERSION_URL = '/version.json';
const POLLING_INTERVAL_VALUE = 1000 * 60 * 5; // 5 minutes

interface VersionResponse {
  version: string;
  branch: string;
  ticket: string;
}

@Injectable({providedIn: 'root'})
export class VersionService {
  private http = inject(HttpClient);
  private snackbarService = inject(SnackbarService);

  private initialVersion;
  private snackbarRef;

  private _branch: string;
  private _ticket: string;

  startPolling() {
    return interval(POLLING_INTERVAL_VALUE).pipe(
      startWith(0), // start immediately when subscribed
      switchMap(() =>
        this.http.get(VERSION_URL, {
          responseType: 'json',
        }).pipe(
          catchError((error: any) => {
            console.error(
              'An error occurred while polling for the latest version:',
              error
            );

            return EMPTY;
          })
        )
      ),
      map((resp: any) => resp?.message || resp),
      tap((body: VersionResponse) => {
        /*
        {
  "version": "20250505140322-dev",
  "branch": "preview/LTF-2553_ad_hoc",
  "ticket": "LTF-2553"
}
         */

        this._branch = body.branch;
        this._ticket = body.ticket;


        // If there's no initial version set, set it to the current version (it means it's the initial load)
        if (!this.initialVersion && body.version) {
          this.initialVersion = body.version;
        }

        // After some time has passed and the new version is different than the old one (stored in memory), trigger the snackbar
        if (body.version !== this.initialVersion) {
          this.triggerNewVersionSnackbar();
        }
      }),
      takeWhile((body: VersionResponse) => body.version === this.initialVersion)
    );
  }

  private triggerNewVersionSnackbar() {
    this.snackbarRef = this.snackbarService.openSnackBar(
      'An updated version of the course is available',
      {
        duration: null,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: 'warning-snackbar-container'
      },
      'Reload'
    );
    this.snackbarRef
      .onAction()
      .pipe(take(1))
      .subscribe(() => {
        window.location.reload();
      });
  }

  public get ticket() {
    return this._ticket;
  }

  public get branch() {
    return this._branch;
  }
}
